import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SnapshotEditForm from './components/SnapshotEditForm';
import ImportModal from './components/ImportModal';
import './SnapshotsDebug.css';
import { API_BASE_URL } from './lib/api';

const SnapshotsDebug = () => {
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSnapshot, setEditingSnapshot] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first
  const [showImportModal, setShowImportModal] = useState(false);
  const [refreshingSnapshot, setRefreshingSnapshot] = useState(null);
  const [deletingSnapshot, setDeletingSnapshot] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchSnapshots();
  }, []);

  const fetchSnapshots = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/snapshots`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSnapshots(response.data.snapshots);
      } else {
        setError('Failed to load snapshots');
      }
    } catch (err) {
      console.error('Error fetching snapshots:', err);
      setError(err.response?.data?.error || 'Failed to fetch snapshots');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (snapshot) => {
    setEditingSnapshot(snapshot);
    setSuccessMessage(null);
  };

  const handleSave = async (date, updateData) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/snapshots/${date}`,
        updateData,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccessMessage(`Snapshot for ${date} updated successfully!`);
        setEditingSnapshot(null);
        await fetchSnapshots(); // Refresh the list

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error updating snapshot:', err);
      throw new Error(err.response?.data?.error || 'Failed to update snapshot');
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Are you sure you want to delete the snapshot for ${date}?`)) {
      return;
    }

    setDeletingSnapshot(date);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/snapshots/${date}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccessMessage(`Snapshot for ${date} deleted successfully!`);
        await fetchSnapshots();
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error deleting snapshot:', err);
      setError(err.response?.data?.error || 'Failed to delete snapshot');
    } finally {
      setDeletingSnapshot(null);
    }
  };

  const handleRefresh = async (date) => {
    if (!window.confirm(`This will refresh the snapshot for ${date} using historical prices. This may take a moment. Continue?`)) {
      return;
    }

    setRefreshingSnapshot(date);
    try {
      setSuccessMessage(`Refreshing snapshot for ${date}...`);

      const response = await axios.post(
        `${API_BASE_URL}/snapshots/${date}/backfill`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccessMessage(`Snapshot for ${date} refreshed successfully!`);
        await fetchSnapshots();

        // Show stats if available
        if (response.data.stats) {
          const stats = response.data.stats;
          setSuccessMessage(
            `Snapshot for ${date} refreshed successfully! ` +
            `(${stats.successfulPrices}/${stats.totalHoldings} prices fetched)`
          );
        }

        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      console.error('Error refreshing snapshot:', err);
      setError(err.response?.data?.error || 'Failed to refresh snapshot');
      setTimeout(() => setError(null), 5000);
    } finally {
      setRefreshingSnapshot(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/holdings/export`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `portfolio-export-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Portfolio exported successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export portfolio');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    setSuccessMessage('Portfolio imported successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
    // Optionally refresh snapshots
    fetchSnapshots();
  };

  const formatCurrency = (value, currency = 'EUR') => {
    if (value === undefined || value === null || isNaN(value)) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true, // Explicitly enable thousand separators
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getFilteredAndSortedSnapshots = () => {
    let filtered = [...snapshots];

    // Filter by date if specified
    if (filterDate) {
      filtered = filtered.filter(s => s.date.includes(filterDate));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortOrder === 'desc') {
        return b.date.localeCompare(a.date);
      } else {
        return a.date.localeCompare(b.date);
      }
    });

    return filtered;
  };

  const displayedSnapshots = getFilteredAndSortedSnapshots();

  // Calculate benchmark performance
  const calculateBenchmarkPerformance = () => {
    if (displayedSnapshots.length < 2) return null;

    const sortedSnapshots = [...displayedSnapshots].sort((a, b) => a.date.localeCompare(b.date));
    const firstSnapshot = sortedSnapshots[0];
    const lastSnapshot = sortedSnapshots[sortedSnapshots.length - 1];

    const firstPortfolio = firstSnapshot.netWorth || 0;
    const lastPortfolio = lastSnapshot.netWorth || 0;
    const portfolioReturn = firstPortfolio ? ((lastPortfolio - firstPortfolio) / firstPortfolio * 100) : 0;

    const benchmarkNames = {
      sp500: 'S&P 500',
      nasdaq: 'NASDAQ',
      russell2000: 'Russell 2000',
      dax: 'DAX',
      stoxx600: 'STOXX 600',
      msciWorld: 'MSCI World',
      bitcoin: 'Bitcoin'
    };

    const benchmarks = {};

    Object.keys(benchmarkNames).forEach(key => {
      const firstValue = firstSnapshot.benchmarks?.[key] || (key === 'sp500' ? firstSnapshot.sp500Value : null);
      const lastValue = lastSnapshot.benchmarks?.[key] || (key === 'sp500' ? lastSnapshot.sp500Value : null);

      if (firstValue && lastValue) {
        const benchmarkReturn = ((lastValue - firstValue) / firstValue * 100);
        const relativePerformance = portfolioReturn - benchmarkReturn;

        benchmarks[key] = {
          name: benchmarkNames[key],
          startValue: firstValue,
          endValue: lastValue,
          return: benchmarkReturn,
          relativePerformance
        };
      }
    });

    return {
      period: `${firstSnapshot.date} to ${lastSnapshot.date}`,
      portfolioReturn,
      benchmarks
    };
  };

  const benchmarkPerformance = calculateBenchmarkPerformance();

  // Toggle row expansion
  const toggleRowExpansion = (date) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Keyboard navigation handler
  const handleKeyDown = (e, snapshot) => {
    // Edit: Ctrl/Cmd + E
    if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
      e.preventDefault();
      handleEdit(snapshot);
    }
    // Refresh: Ctrl/Cmd + R
    if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
      e.preventDefault();
      handleRefresh(snapshot.date);
    }
    // Delete: Delete key
    if (e.key === 'Delete') {
      e.preventDefault();
      handleDelete(snapshot.date);
    }
  };

  if (loading) {
    return (
      <div className="snapshots-debug">
        <div className="loading">Loading snapshots...</div>
      </div>
    );
  }

  return (
    <div className="snapshots-debug">
      <div className="debug-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1>Snapshots Debug Panel</h1>
            <span className="snapshot-count-badge">{displayedSnapshots.length}</span>
          </div>
          <p className="subtitle">View and edit historical portfolio snapshots</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', padding: '4px', background: 'rgba(79, 70, 229, 0.1)', borderRadius: '6px' }}>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-import"
              title="Import portfolio holdings"
            >
              ↓ Import
            </button>
            <button
              onClick={handleExport}
              className="btn-export"
              title="Export portfolio holdings"
            >
              ↑ Export
            </button>
          </div>
          <button onClick={() => navigate('/')} className="btn-back">
            ← Back
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {benchmarkPerformance && (
        <div className="benchmark-performance-section">
          <h2>📊 Benchmark Performance</h2>
          <p className="period">Period: {benchmarkPerformance.period}</p>
          <div className="performance-grid">
            <div className="performance-card portfolio-card">
              <h3>Your Portfolio</h3>
              <div className={`return-value ${benchmarkPerformance.portfolioReturn >= 0 ? 'positive' : 'negative'}`}>
                {benchmarkPerformance.portfolioReturn >= 0 ? '+' : ''}{benchmarkPerformance.portfolioReturn.toFixed(2)}%
              </div>
            </div>
            {Object.entries(benchmarkPerformance.benchmarks)
              .filter(([key, data]) => data && data.startValue && data.endValue)
              .map(([key, data]) => (
                <div key={key} className="performance-card">
                  <h3>{data.name}</h3>
                  <div className={`return-value ${data.return >= 0 ? 'positive' : 'negative'}`}>
                    {data.return >= 0 ? '+' : ''}{data.return.toFixed(2)}%
                  </div>
                  <div className={`relative-performance ${data.relativePerformance >= 0 ? 'outperforming' : 'underperforming'}`}>
                    {data.relativePerformance >= 0 ? '🎯' : '📉'} {data.relativePerformance >= 0 ? '+' : ''}{data.relativePerformance.toFixed(2)}%
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="filterDate">Filter by Date:</label>
          <input
            type="date"
            id="filterDate"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="sortOrder">Sort Order:</label>
          <select
            id="sortOrder"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
        <div className="stats">
          <strong>Total Snapshots:</strong> {displayedSnapshots.length}
        </div>
      </div>

      {displayedSnapshots.length === 0 ? (
        <div className="no-data">
          {filterDate ? 'No snapshots match your filter.' : 'No snapshots found.'}
        </div>
      ) : (
        <div className="snapshots-table-wrapper">
          <table className="snapshots-table">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Net Worth</th>
                <th>Total Assets</th>
                <th>Total Liabilities</th>
                <th>Currency</th>
                <th>Stale Data</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedSnapshots.map((snapshot) => (
                <React.Fragment key={snapshot.date}>
                <tr
                  className={snapshot.containsStaleData ? 'stale-row' : ''}
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, snapshot)}
                  aria-label={`Snapshot for ${formatDate(snapshot.date)}, Net worth ${formatCurrency(snapshot.netWorth, snapshot.currency)}`}
                >
                  <td className="expand-cell">
                    <button
                      onClick={() => toggleRowExpansion(snapshot.date)}
                      className="expand-button"
                      aria-label={expandedRows.has(snapshot.date) ? "Collapse details" : "Expand details"}
                      title={expandedRows.has(snapshot.date) ? "Hide details" : "Show benchmarks and categories"}
                    >
                      {expandedRows.has(snapshot.date) ? '▼' : '▶'}
                    </button>
                  </td>
                  <td className="date-cell" data-label="Date">
                    <div className="date-primary">{formatDate(snapshot.date)}</div>
                    {(snapshot.timestamp || snapshot.createdAt) && (
                      <small className="timestamp">
                        🕒 {formatDateTime(snapshot.timestamp || snapshot.createdAt)}
                      </small>
                    )}
                  </td>
                  <td className="currency-cell" data-label="Net Worth">
                    {formatCurrency(snapshot.netWorth, snapshot.currency)}
                  </td>
                  <td className="currency-cell" data-label="Total Assets">
                    {formatCurrency(snapshot.totalAssets, snapshot.currency)}
                  </td>
                  <td className="currency-cell" data-label="Total Liabilities">
                    {formatCurrency(snapshot.totalLiabilities, snapshot.currency)}
                  </td>
                  <td data-label="Currency">{snapshot.currency}</td>
                  <td data-label="Stale Data">
                    {snapshot.containsStaleData ? (
                      <span className="badge badge-warning">
                        Yes ({snapshot.staleCategoriesCount || 0})
                      </span>
                    ) : (
                      <span className="badge badge-success">No</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEdit(snapshot)}
                      className="btn-edit"
                      title="Edit snapshot"
                      disabled={refreshingSnapshot === snapshot.date || deletingSnapshot === snapshot.date}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRefresh(snapshot.date)}
                      className={`btn-refresh ${refreshingSnapshot === snapshot.date ? 'loading' : ''}`}
                      title="Refresh snapshot with historical prices"
                      disabled={refreshingSnapshot === snapshot.date}
                    >
                      {refreshingSnapshot === snapshot.date ? '⟳' : 'Refresh'}
                    </button>
                    <button
                      onClick={() => handleDelete(snapshot.date)}
                      className={`btn-delete ${deletingSnapshot === snapshot.date ? 'loading' : ''}`}
                      title="Delete snapshot"
                      disabled={deletingSnapshot === snapshot.date}
                    >
                      {deletingSnapshot === snapshot.date ? '...' : 'Delete'}
                    </button>
                  </td>
                </tr>
                {expandedRows.has(snapshot.date) && (
                  <tr className="expanded-row">
                    <td colSpan="8">
                      <div className="expanded-details">
                        <div className="details-section">
                          <h4>📊 Benchmarks</h4>
                          <div className="benchmark-grid">
                            {snapshot.benchmarks && Object.keys(snapshot.benchmarks).length > 0 ? (
                              <>
                                {snapshot.benchmarks.sp500 && (
                                  <div className="detail-item">
                                    <span className="detail-label">S&P 500:</span>
                                    <span className="detail-value">{snapshot.benchmarks.sp500.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.nasdaq && (
                                  <div className="detail-item">
                                    <span className="detail-label">NASDAQ:</span>
                                    <span className="detail-value">{snapshot.benchmarks.nasdaq.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.russell2000 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Russell 2000:</span>
                                    <span className="detail-value">{snapshot.benchmarks.russell2000.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.dax && (
                                  <div className="detail-item">
                                    <span className="detail-label">DAX:</span>
                                    <span className="detail-value">{snapshot.benchmarks.dax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.stoxx600 && (
                                  <div className="detail-item">
                                    <span className="detail-label">STOXX 600:</span>
                                    <span className="detail-value">{snapshot.benchmarks.stoxx600.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.msciWorld && (
                                  <div className="detail-item">
                                    <span className="detail-label">MSCI World:</span>
                                    <span className="detail-value">{snapshot.benchmarks.msciWorld.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                {snapshot.benchmarks.bitcoin && (
                                  <div className="detail-item">
                                    <span className="detail-label">Bitcoin:</span>
                                    <span className="detail-value">${snapshot.benchmarks.bitcoin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-muted">No benchmark data available</p>
                            )}
                          </div>
                        </div>
                        <div className="details-section">
                          <h4>📁 Categories</h4>
                          <div className="category-grid">
                            {snapshot.categories ? (
                              <>
                                {snapshot.categories.stocks?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Stocks:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.stocks.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.crypto?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Crypto:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.crypto.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.preciousMetals?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Precious Metals:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.preciousMetals.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.cash?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Cash:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.cash.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.realEstate?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Real Estate:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.realEstate.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.other?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Other:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.other.value, snapshot.currency)}</span>
                                  </div>
                                )}
                                {snapshot.categories.liabilities?.value > 0 && (
                                  <div className="detail-item">
                                    <span className="detail-label">Liabilities:</span>
                                    <span className="detail-value">{formatCurrency(snapshot.categories.liabilities.value, snapshot.currency)}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-muted">No category data available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingSnapshot && (
        <SnapshotEditForm
          snapshot={editingSnapshot}
          onSave={handleSave}
          onCancel={() => setEditingSnapshot(null)}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default SnapshotsDebug;
